import React, { useRef, useEffect } from 'react';
import { FileText, PieChart, Search, TrendingUp, BarChart3, GitCompare } from 'lucide-react';

const Features = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (!sectionRef.current) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );
    
    const elements = sectionRef.current.querySelectorAll('.reveal');
    elements.forEach(el => observer.observe(el));
    
    return () => {
      elements.forEach(el => observer.unobserve(el));
    };
  }, []);

  const features = [
    {
      icon: <FileText className="w-6 h-6 text-blue-600" />,
      title: "AI-Powered Data Extraction",
      description: "Auto-parses quarterly reports, PCAP statements, and fund updates, saving 100+ hours of manual work per fund per year.",
      delay: "100ms"
    },
    {
      icon: <BarChart3 className="w-6 h-6 text-blue-600" />,
      title: "Live Fund Performance Dashboard",
      description: "Track NAV, MOIC, IRR, and EBITDA trends in real-time. Compare fund performance across quarters & industries.",
      delay: "200ms"
    },
    {
      icon: <PieChart className="w-6 h-6 text-blue-600" />,
      title: "Attribution Analysis & AI-Driven Insights",
      description: "Identify which portfolio companies drive fund returns. Get red flag alerts for declining metrics.",
      delay: "300ms"
    },
    {
      icon: <Search className="w-6 h-6 text-blue-600" />,
      title: "Advanced Search & Portfolio Insights",
      description: "Ask natural language questions like 'What's my exposure to AI startups?' and get instant answers.",
      delay: "400ms"
    },
    {
      icon: <TrendingUp className="w-6 h-6 text-blue-600" />,
      title: "Historical Performance Tracking",
      description: "Track the evolution of key metrics over time with interactive visualizations and trend identification.",
      delay: "500ms"
    },
    {
      icon: <GitCompare className="w-6 h-6 text-blue-600" />,
      title: "Real-Time Benchmarks & Comparisons",
      description: "Compare fund performance against industry benchmarks and similar funds to gain competitive insights.",
      delay: "600ms"
    }
  ];

  return (
    <section
      id="features"
      ref={sectionRef}
      className="py-24 relative overflow-hidden"
    >
      {/* Background Grid */}
      <div className="absolute inset-0 data-grid opacity-30"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-8">
          <h2 className="heading-lg mb-6 reveal opacity-0 transition-all duration-700 translate-y-8">
            Powerful Features for Alternative Investment Analytics
          </h2>
          <p className="text-gray-600 text-lg reveal opacity-0 transition-all duration-700 translate-y-8" style={{ transitionDelay: '100ms' }}>
            Portco AI's Query interface helps you understand everything about your portfolio in one place - just like a real analyst
          </p>
        </div>
        
        <div className="max-w-5xl mx-auto mb-16 reveal opacity-0 transition-all duration-700 translate-y-8" style={{ transitionDelay: '200ms' }}>
          <img 
            src="/lovable-uploads/4de3dd01-8fc3-4c4e-aaad-d36369c1409e.png" 
            alt="Portco AI Query Interface" 
            className="w-full h-auto rounded-xl shadow-lg"
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {features.map((feature, index) => (
            <div 
              key={index} 
              className="feature-card hover-lift reveal opacity-0 transition-all duration-700 translate-y-8"
              style={{ transitionDelay: feature.delay }}
            >
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                {feature.icon}
              </div>
              <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
              <p className="text-gray-600">
                {feature.description}
              </p>
              <div className="mt-6">
                <a href="#demo" className="text-blue-600 font-medium text-sm flex items-center group">
                  Learn more
                  <svg 
                    className="ml-1 w-4 h-4 transition-transform group-hover:translate-x-1" 
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m9 18 6-6-6-6"/>
                  </svg>
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
